import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGetReportSectionQueryKey,
  getReportSection,
  useCreateCustomReport,
  useGetCustomReport,
} from "~/api/custom-reports/custom-reports";
import { useListProjects } from "~/api/folders/folders";
import { ReportSectionStatus, ReportStatus } from "~/api/model";
import { generateIdempotencyKey } from "~/features/chat/components/reportArea/reportUtils";
import logger from "~/utils/logger";

const REPORT_POLL_INTERVAL_MS = 3000;
const REPORT_TIMEOUT_MS = 180_000;

type DemoReportStatus = "idle" | "generating" | "ready" | "error";

interface UseFtueDemoReportReturn {
  content: string | null;
  status: DemoReportStatus;
  error: string | null;
  caseId: string | null;
  reportId: string | null;
  generate: () => void;
  cancel: () => void;
}

const isSectionCompleted = (status: string | undefined): boolean =>
  status === ReportSectionStatus.completed;

export const useFtueDemoReport = (
  organizationId: string,
  templateId: string | null
): UseFtueDemoReportReturn => {
  const [caseId, setCaseId] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lazy listing — fetched on demand from `generate()`
  const { refetch: refetchProjects } = useListProjects(
    organizationId,
    { archived: false, limit: 1 },
    { query: { enabled: false } }
  );
  const refetchProjectsRef = useRef(refetchProjects);
  refetchProjectsRef.current = refetchProjects;

  const {
    mutate: createReport,
    isPending: isCreatingReport,
    isError: isCreateError,
    reset: resetCreate,
  } = useCreateCustomReport();
  const createReportRef = useRef(createReport);
  createReportRef.current = createReport;
  const resetCreateRef = useRef(resetCreate);
  resetCreateRef.current = resetCreate;

  const isPollingEnabled =
    !!organizationId && !!caseId && !!reportId && !isCancelled && !isTimedOut;

  const { data: reportData, isError: isReportQueryError } = useGetCustomReport(
    organizationId,
    caseId ?? "",
    reportId ?? "",
    {
      query: {
        enabled: isPollingEnabled,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return REPORT_POLL_INTERVAL_MS;

          const reportTerminal =
            data.status === ReportStatus.completed ||
            data.status === ReportStatus.failed;

          const sections = data.reportSections ?? [];
          const sectionsTerminal =
            sections.length > 0 &&
            sections.every(
              (s) =>
                s.status === ReportSectionStatus.completed ||
                s.status === ReportSectionStatus.failed
            );

          // Keep polling while either the report or any section is still in flight.
          // This lets the streaming UI grow as sections complete one by one.
          if (reportTerminal && (sections.length === 0 || sectionsTerminal)) {
            return false;
          }
          return REPORT_POLL_INTERVAL_MS;
        },
        refetchIntervalInBackground: true,
      },
    }
  );

  // Fetch each completed section's full content in parallel. Pending/failed
  // sections are still listed (keyed by id) so React can keep query slots stable.
  const sectionMetas = useMemo(
    () => reportData?.reportSections ?? [],
    [reportData?.reportSections]
  );

  const sectionQueries = useQueries({
    queries: sectionMetas.map((section) => ({
      queryKey: getGetReportSectionQueryKey(
        organizationId,
        caseId ?? "",
        reportId ?? "",
        section.id
      ),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getReportSection(
          organizationId,
          caseId ?? "",
          reportId ?? "",
          section.id,
          undefined,
          signal
        ),
      enabled:
        isPollingEnabled && !!section.id && isSectionCompleted(section.status),
      staleTime: Infinity,
    })),
  });

  const assembledContent = useMemo(() => {
    if (sectionMetas.length === 0) return null;

    const sortedIndices = sectionMetas
      .map((_, idx) => idx)
      .sort(
        (a, b) => (sectionMetas[a].order ?? 0) - (sectionMetas[b].order ?? 0)
      );

    const parts: string[] = [];
    for (const idx of sortedIndices) {
      const meta = sectionMetas[idx];
      if (!isSectionCompleted(meta.status)) continue;

      const sectionData = sectionQueries[idx]?.data;
      const body = sectionData?.content?.trim();
      if (!body) continue;

      const heading = meta.title?.trim();
      parts.push(heading ? `# ${heading}\n\n${body}` : body);
    }

    return parts.length > 0 ? parts.join("\n\n") : null;
  }, [sectionMetas, sectionQueries]);

  // Prefer the server-assembled `text` when present; fall back to the
  // streaming concat of section bodies.
  const content = reportData?.text?.trim() ? reportData.text : assembledContent;

  const allSectionsLoaded =
    sectionMetas.length > 0 &&
    sectionMetas.every((meta, idx) => {
      if (!isSectionCompleted(meta.status)) return false;
      const q = sectionQueries[idx];
      return !!q && !q.isLoading && !q.isError;
    });

  const isReportComplete =
    reportData?.status === ReportStatus.completed &&
    (!!reportData?.text?.trim() ||
      sectionMetas.length === 0 ||
      allSectionsLoaded);

  // Timeout guard: arm when polling begins, clear when terminal/cancelled
  useEffect(() => {
    if (!isPollingEnabled || isReportComplete) return;

    timeoutRef.current = setTimeout(() => {
      setIsTimedOut(true);
      logger.error("Demo report generation timed out");
    }, REPORT_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isPollingEnabled, isReportComplete]);

  const deriveStatus = (): DemoReportStatus => {
    if (isCancelled && !reportId && !isCreatingReport) return "idle";
    if (isTimedOut) return "error";
    if (listError) return "error";
    if (isCreateError) return "error";
    if (isReportQueryError) return "error";
    if (reportData?.status === ReportStatus.failed) return "error";
    if (isReportComplete) return "ready";
    if (isCreatingReport || reportId) return "generating";
    return "idle";
  };

  const status = deriveStatus();

  const deriveError = (): string | null => {
    if (isTimedOut) return "Demo report generation timed out";
    if (listError) return listError;
    if (isCreateError) return "Failed to start demo report generation";
    if (reportData?.status === ReportStatus.failed)
      return "Demo report generation failed";
    if (isReportQueryError) return "Failed to poll demo report status";
    return null;
  };

  const error = deriveError();

  const generate = useCallback(() => {
    if (!templateId || !organizationId) return;

    setIsCancelled(false);
    setIsTimedOut(false);
    setListError(null);
    setCaseId(null);
    setReportId(null);

    void (async () => {
      try {
        const { data, error: refetchError } = await refetchProjectsRef.current({
          throwOnError: false,
        });

        if (refetchError) {
          logger.error("Failed to list cases for demo report", refetchError);
          setListError("Failed to load cases");
          return;
        }

        const firstCase = data?.projects?.[0];
        if (!firstCase) {
          setListError("No cases available for demo report");
          return;
        }

        setCaseId(firstCase.id);

        createReportRef.current(
          {
            organizationId,
            projectId: firstCase.id,
            data: {
              idempotencyKey: generateIdempotencyKey(),
              templateId,
              isPreviewReport: true,
            },
          },
          {
            onSuccess: (report) => {
              setReportId(report.id);
            },
            onError: (err) => {
              logger.error("Failed to create demo report", err);
            },
          }
        );
      } catch (err) {
        logger.error("Demo report generation error", err);
        setListError("Failed to start demo report generation");
      }
    })();
  }, [organizationId, templateId]);

  const cancel = useCallback(() => {
    setIsCancelled(true);
    setCaseId(null);
    setReportId(null);
    setListError(null);
    resetCreateRef.current();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { content, status, error, caseId, reportId, generate, cancel };
};
